import { supabase } from "@/integrations/supabase/client";
import {
  handleSupabaseFunctionError,
  InsufficientCreditsError,
} from "@/utils/supabaseFunctionsError";
import type {
  EvaluationRubricsResponse,
  MaturityEvaluation,
  MaturityFrameworkResponse,
  QuestionnaireResponse,
  ScoringAlgorithm,
} from "@/types/maturity";

export async function getMaturityFramework(): Promise<MaturityFrameworkResponse> {
  const { data, error } = await supabase.functions.invoke(
    "api/maturity/framework",
    { method: "GET" }
  );
  await handleSupabaseFunctionError(error);
  if (!data?.success)
    throw new Error(data?.error || "Failed to load framework");
  return data.data;
}

export async function getScoringAlgorithm(): Promise<ScoringAlgorithm> {
  const { data, error } = await supabase.functions.invoke(
    "api/maturity/scoring-algorithm",
    { method: "GET" }
  );
  await handleSupabaseFunctionError(error);
  if (!data?.success)
    throw new Error(data?.error || "Failed to load scoring algorithm");
  return data.data;
}

export async function getQuestionnaireItems(
  assuranceLevel: string
): Promise<QuestionnaireResponse> {
  const params = new URLSearchParams({ assurance_level: assuranceLevel });
  const { data, error } = await supabase.functions.invoke(
    `api/maturity/questionnaire?${params.toString()}`,
    { method: "GET" }
  );
  await handleSupabaseFunctionError(error);
  if (!data?.success)
    throw new Error(data?.error || "Failed to load questionnaire");
  return data.data;
}

export async function getEvaluationRubrics(
  categoryCode?: string,
  domainCode?: string
): Promise<EvaluationRubricsResponse> {
  const params = new URLSearchParams();
  if (categoryCode) params.set("category_code", categoryCode);
  if (domainCode) params.set("domain_code", domainCode);
  const { data, error } = await supabase.functions.invoke(
    `api/maturity/evaluation-rubrics?${params.toString()}`,
    { method: "GET" }
  );
  await handleSupabaseFunctionError(error);
  if (!data?.success) throw new Error(data?.error || "Failed to load rubrics");
  return data.data;
}

export async function getMaturityEvaluation(
  sessionId: string
): Promise<MaturityEvaluation> {
  const { data, error } = await supabase.functions.invoke(
    `api/maturity/evaluation/${sessionId}`,
    { method: "GET" }
  );
  await handleSupabaseFunctionError(error);
  if (!data?.success)
    throw new Error(data?.error || "Failed to load evaluation");
  return data.data;
}

export async function savePreliminary(
  sessionId: string,
  preliminaryResponses: Record<string, boolean>
): Promise<{ updated_at: string }> {
  const { data, error } = await supabase.functions.invoke(
    `api/maturity/evaluation/${sessionId}/preliminary`,
    {
      method: "PUT",
      body: { preliminary_responses: preliminaryResponses },
    }
  );
  await handleSupabaseFunctionError(error);
  if (!data?.success)
    throw new Error(data?.error || "Failed to save preliminary");
  return data;
}

export async function finalizePreliminary(
  sessionId: string
): Promise<MaturityEvaluation> {
  const { data, error } = await supabase.functions.invoke(
    `api/maturity/evaluation/${sessionId}/preliminary/finalize`,
    { method: "POST" }
  );
  await handleSupabaseFunctionError(error);
  if (!data?.success) {
    if (data?.error === "insufficient_credits") {
      throw new InsufficientCreditsError({
        shortage: data.shortage ?? 0,
        required: data.required ?? 0,
        current: data.current_balance ?? 0,
      });
    }
    throw new Error(data?.error || "Failed to finalize preliminary");
  }
  return data.data;
}

export async function saveFullEval(
  sessionId: string,
  fullResponses: Record<string, number>,
  fullNotes: Record<string, string>
): Promise<{ updated_at: string }> {
  const { data, error } = await supabase.functions.invoke(
    `api/maturity/evaluation/${sessionId}/full`,
    {
      method: "PUT",
      body: { full_responses: fullResponses, full_notes: fullNotes },
    }
  );
  await handleSupabaseFunctionError(error);
  if (!data?.success)
    throw new Error(data?.error || "Failed to save full eval");
  return data;
}

export async function finalizeFullEval(
  sessionId: string
): Promise<{ data: MaturityEvaluation; validation: Record<string, unknown> }> {
  const { data, error } = await supabase.functions.invoke(
    `api/maturity/evaluation/${sessionId}/full/finalize`,
    { method: "POST" }
  );
  await handleSupabaseFunctionError(error);
  if (!data?.success) {
    if (data?.error === "insufficient_credits") {
      throw new InsufficientCreditsError({
        shortage: data.shortage ?? 0,
        required: data.required ?? 150,
        current: data.current_balance ?? 0,
      });
    }
    throw new Error(data?.error || "Failed to finalize full eval");
  }
  return data;
}

export async function resetFullEval(
  sessionId: string
): Promise<MaturityEvaluation> {
  const { data, error } = await supabase.functions.invoke(
    `api/maturity/evaluation/${sessionId}/full/reset`,
    { method: "POST" }
  );
  await handleSupabaseFunctionError(error);
  if (!data?.success)
    throw new Error(data?.error || "Failed to reset full eval");
  return data.data;
}
